import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import "./SelectPricingPlan.css";
import "./PricingPlanList.css";

const PricingPlanList = ({ onEditPlan, updateTrigger, setLoader }) => {
    const [rowsData, setRowsData] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

    // Delete Dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);

    // Assign Properties Dialog
    const [assignPropertiesPopup, setassignPropertiesPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [assignType, setAssignType] = useState("zip");
    const [selectedZipCodes, setSelectedZipCodes] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [filteredCities, setFilteredCities] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [allZipCodes, setAllZipCodes] = useState([]);
    const [rawProperties, setRawProperties] = useState([]);
    const [allProperties, setAllProperties] = useState([]);
    const [states, setstates] = useState([]);
    const [cities, setcities] = useState([]);
    const [alreadyAssignedProperties, setAlreadyAssignedProperties] = useState([]);
    const [assignError, setAssignError] = useState("");

    // View Properties Dialog
    const [openPropertiesPopup, setOpenPropertiesPopup] = useState(false);

    const hostPath = process.env.REACT_APP_API_BASE_URL;
    const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;
    const weekOrder = [1, 2, 3, 4, 5, 6, 0];

    const SELECT_ALL = {
        label: "Select All",
        value: "__ALL__"
    };

    const getOptionValue = (option) => {
        if (option === SELECT_ALL || option?.value === SELECT_ALL.value) return SELECT_ALL.value;
        return option?.value ?? option;
    };

    const getOptionLabel = (option) => {
        if (option === SELECT_ALL || option?.value === SELECT_ALL.value) return SELECT_ALL.label;
        return option?.label ?? String(option ?? "");
    };

    const readApiError = async (response, fallbackMessage) => {
        const text = await response.text().catch(() => "");
        if (!text) return fallbackMessage;

        try {
            const data = JSON.parse(text);
            return data?.message || data?.responseMessage || data?.error || text;
        } catch {
            return text;
        }
    };

    const fetchJsonOrThrow = async (url, options, fallbackMessage) => {
        const response = await fetch(url, options);
        if (!response.ok) {
            throw new Error(await readApiError(response, fallbackMessage));
        }
        return response;
    };

    const dayMap = {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday"
    };

    // ================= FETCH PRICING PLANS =================
    const fetchPricingPlans = async () => {
        setLoader(true);
        try {
            const response = await fetch(`${hostPath}/api/GetAllPricingPlans`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                }
            });

            const data = await response.json();

            const formattedData = await Promise.all(
                data.map(async (item) => {
                    try {
                        const countResponse = await fetch(
                            `${hostPath}/api/GetPropertyCountByPricingPlan`,
                            {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    "x-functions-key": hostHeader
                                },
                                body: JSON.stringify({ pricingPlanName: item.pricingPlanName })
                            }
                        );

                        const countData = await countResponse.json();

                        return {
                            ...item,
                            propertyCount: countData.propertyCount ?? 0
                        };
                    } catch (err) {
                        return {
                            ...item,
                            propertyCount: 0,
                            rulesCount: item.rules?.length ?? 0
                        };
                    }
                })
            );

            setRowsData(formattedData);
        } catch (error) {
            console.error("Error fetching pricing plans:", error);
            setRowsData([]);
        } finally {
            setLoader(false);
        }
    };

    // ✅ Fetch on mount AND when updateTrigger changes
    useEffect(() => {
        fetchPricingPlans();
    }, [updateTrigger]);

    // ================= HANDLERS =================

    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRowForMenu(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRowForMenu(null);
    };

    const handleDeletePlan = async (row) => {
    setPlanToDelete(row);
    try {
        setPlanToDelete({ ...row, propertyCount: row.propertyCount ?? 0 });
    } catch (err) {
        setPlanToDelete({ ...row, propertyCount: 0 });
    } finally {
        setOpenDeleteDialog(true);
        handleMenuClose();
    }
};

    const handleDeleteConfirm = async (type) => {
        if (type === "cancel") {
            setOpenDeleteDialog(false);
            setPlanToDelete(null);
            return;
        }

        setLoader(true);
        try {
            await fetchJsonOrThrow(`${hostPath}/api/DeletePricingPlan`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                },
                body: JSON.stringify({
                    id: planToDelete?.id,
                    pricingPlanID: planToDelete?.pricingPlanID
                })
            }, "Failed to delete pricing plan");

            await fetchPricingPlans(); // Refresh
        } catch (error) {
            console.error("Error deleting pricing plan:", error);
            alert(error.message || "Failed to delete pricing plan");
        } finally {
            setOpenDeleteDialog(false);
            setPlanToDelete(null);
            setLoader(false);
        }
    };

    const handleAssignClick = async (row) => {
        setLoader(true);
        try {
            await fetchZipCodes();
            const assignedProps = await fetchPropertyDetails(row.pricingPlanName.trim());
            setAlreadyAssignedProperties(assignedProps);
            setSelectedRow(row);
            setAssignType("zip");
            setSelectedZipCodes([]);
            setSelectedProperties([]);
            setAssignError("");
            setassignPropertiesPopup(true);
        } catch (error) {
            console.error("Failed to load assign data", error);
        } finally {
            setLoader(false);
        }
    };

    const handleViewProperties = async (row) => {
        setLoader(true);
        try {
            const properties = await fetchPropertyDetails(row.pricingPlanName.trim());
            setSelectedRow({ ...row, properties });
            setOpenPropertiesPopup(true);
        } catch (error) {
            console.error("Failed to fetch properties", error);
        } finally {
            setLoader(false);
        }
    };

    const handleAssignPlan = async () => {
        if (!selectedRow) return;
        setAssignError("");

        if (assignType === "property" && selectedProperties.length > 0) {
            const assignedNames = alreadyAssignedProperties.map(p => String(p.nickName).toLowerCase());
            const assignedIds = alreadyAssignedProperties.map(p => p.parking_Lot_ID);
            const duplicates = selectedProperties.filter(
                p => assignedIds.includes(p.value) || assignedNames.includes(String(p.label).toLowerCase())
            );
            if (duplicates.length > 0) {
                setAssignError(
                    `Property "${duplicates.map(d => d.label).join('", "')}" is already assigned to this pricing plan.`
                );
                return;
            }
        }

        setLoader(true);

        try {
            if (assignType === "zip") {
                await assignPlanByZip();
            } else if (assignType === "property") {
                await assignPlanByProperty();
            }

            await fetchPricingPlans(); // Refresh
            setassignPropertiesPopup(false);
            setSelectedZipCodes([]);
            setSelectedProperties([]);
        } catch (error) {
            console.error("Assignment failed", error);
        } finally {
            setLoader(false);
        }
    };

    const firstDefined = (...values) =>
        values.find(value => value !== undefined && value !== null && value !== "");

    const handleRemoveProperty = async (property) => {
        setLoader(true);
        try {
            const parkingLotId = firstDefined(
                property.parking_Lot_ID,
                property.parkingLotID,
                property.parkingLotId,
                property.ParkingLotID,
                property.ParkingLotId,
                property.parking_Lot_ID
            );

            await resetPropertyToDefaultPlan({
                pricingPlanId: selectedRow?.pricingPlanID,
                pricingPlanName: selectedRow?.pricingPlanName,
                parkingLotId,
                nickName: property.nickName,
                zipcode: property.zipcode || property.zipCode || property.Zipcode || property.ZipCode
            });

            await fetchPricingPlans(); // Refresh
            const properties = await fetchPropertyDetails(selectedRow.pricingPlanName.trim());
            setSelectedRow(prev => ({ ...prev, properties }));
        } catch (err) {
            console.error("Remove failed", err);
            alert(err.message || "Failed to remove property");
        } finally {
            setLoader(false);
        }
    };

    // ================= API CALLS =================

    const fetchPropertyDetails = async (pricingPlanName) => {
        try {
            const response = await fetch(`${hostPath}/api/GetPropertyDetailsByPricingPlan`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                },
                body: JSON.stringify({ pricingPlanName })
            });

            if (!response.ok) throw new Error("Failed to fetch property details");
            const data = await response.json();
            return data.properties || [];
        } catch (error) {
            console.error("Error fetching property details:", error);
            return [];
        }
    };

    const fetchZipCodes = async () => {
        try {
            const response = await fetch(`${hostPath}/api/GetAllZipcodesAndNicknamesFromZipCodecontainer`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                }
            });

            if (!response.ok) throw new Error("Failed to fetch ZIP codes");
            const data = await response.json();

            const normalizedProperties = data.map(p => ({
                label: p.nickName,
                value: p.parking_Lot_ID || p.parkingLotID || p.parkingLotId || p.ParkingLotID,
                city: p.city,
                state: p.state,
                zipcode: p.zipcode || p.zipCode || p.Zipcode || p.ZipCode
            }));

            setRawProperties(data);
            setAllZipCodes([
                ...new Set(data.map(item => item.zipcode || item.zipCode || item.Zipcode || item.ZipCode).filter(Boolean))
            ].sort());
            setAllProperties(normalizedProperties);
            setstates([...new Set(data.map(item => item.state).filter(Boolean))].sort());
            setcities(data);
        } catch (error) {
            console.error("Error fetching ZIP codes:", error);
            setAllZipCodes([]);
        }
    };

    const assignPlanByZip = async () => {
        if (!selectedZipCodes.length) {
            alert("Please select at least one ZIP code");
            return;
        }

        const zipCodes = selectedZipCodes.map(zip => String(zip).trim()).filter(Boolean);

        if (!zipCodes.length) {
            alert("Please select at least one ZIP code");
            return;
        }

        const selectedZipSet = new Set(zipCodes.map(zip => String(zip)));
        const parkingLotIds = rawProperties
            .filter(property => selectedZipSet.has(String(property.zipcode || property.zipCode || property.Zipcode || property.ZipCode)))
            .map(property => Number(property.parking_Lot_ID || property.parkingLotID || property.parkingLotId || property.ParkingLotID))
            .filter(id => Number.isInteger(id) && id > 0);

        const payload = {
            ZipCodes: zipCodes,
            ZipCode: zipCodes[0],
            Zipcodes: zipCodes,
            Zipcode: zipCodes[0],
            zipcodes: zipCodes,
            zipcode: zipCodes[0],
            PricingPlanID: selectedRow.pricingPlanID,
            PricingPlanId: selectedRow.pricingPlanID,
            pricingPlanID: selectedRow.pricingPlanID,
            pricingPlanId: selectedRow.pricingPlanID,
            PricingPlanName: selectedRow.pricingPlanName,
            pricingPlanName: selectedRow.pricingPlanName,
            ParkingLotIds: parkingLotIds,
            ParkingLotIDs: parkingLotIds,
            parkingLotIds
        };

        try {
            await fetchJsonOrThrow(`${hostPath}/api/AssignPricingPlanForAllZipcodes`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                },
                body: JSON.stringify(payload)
            }, "Failed to assign pricing plan by ZIP code");
        } catch (zipError) {
            if (!parkingLotIds.length) throw zipError;

            await fetchJsonOrThrow(`${hostPath}/api/AssignPricingPlanWithParkingLots`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                },
                body: JSON.stringify({
                    PricingPlanID: selectedRow.pricingPlanID,
                    PricingPlanName: selectedRow.pricingPlanName,
                    ParkingLotIds: parkingLotIds
                })
            }, zipError.message || "Failed to assign pricing plan by ZIP code");
        }
    };

    const assignPlanByProperty = async () => {
        if (!selectedProperties.length) {
            alert("Please select at least one property");
            return;
        }

        const parkingLotIds = selectedProperties
            .map(p => Number(p.value))
            .filter(id => Number.isInteger(id) && id > 0);

        if (!parkingLotIds.length) {
            alert("Invalid parking lot selection");
            return;
        }

        const payload = {
            PricingPlanID: selectedRow.pricingPlanID,
            PricingPlanName: selectedRow.pricingPlanName,
            ParkingLotIds: parkingLotIds
        };

        await fetchJsonOrThrow(`${hostPath}/api/AssignPricingPlanWithParkingLots`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-functions-key": hostHeader
            },
            body: JSON.stringify(payload)
        }, "Failed to assign pricing plan to properties");
    };

    const resetPropertyToDefaultPlan = async ({ pricingPlanId, pricingPlanName, parkingLotId, nickName, zipcode }) => {
        const payload = {
            PricingPlanID: pricingPlanId,
            PricingPlanId: pricingPlanId,
            pricingPlanID: pricingPlanId,
            pricingPlanId,
            PricingPlanName: pricingPlanName,
            pricingPlanName,
            ParkingLotID: parkingLotId,
            ParkingLotId: parkingLotId,
            parkingLotID: parkingLotId,
            parkingLotId,
            ParkingLotIds: parkingLotId ? [parkingLotId] : [],
            Parking_Lot_ID: parkingLotId,
            NickName: nickName,
            nickName,
            ZipCode: zipcode,
            Zipcode: zipcode,
            zipcode
        };

        let lastError;
        for (const method of ["PUT", "POST"]) {
            try {
                await fetchJsonOrThrow(`${hostPath}/api/ResetPropertyToDefaultPlan`, {
                    method,
                    headers: {
                        "Content-Type": "application/json",
                        "x-functions-key": hostHeader
                    },
                    body: JSON.stringify(payload)
                }, "Failed to remove assigned pricing plan");
                return;
            } catch (error) {
                lastError = error;
            }
        }

        throw lastError || new Error("Failed to remove assigned pricing plan");
    };

    // ================= UTILITY FUNCTIONS =================

    const formatDays = (arr) =>
        weekOrder.filter(d => arr.includes(d)).map(d => dayMap[d]).join(", ");

    const formatTime = (timeString) => {
        const [hours, minutes] = timeString.split(":");
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes));
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getPriceRange = (rules) => {
        if (!rules || rules.length === 0) return "—";
        const prices = rules.map(r => Number(r.price));
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        return min === max ? `$${min}` : `$${min} - $${max}`;
    };

    // ================= EFFECTS =================

    useEffect(() => {
        if (!selectedState) {
            setFilteredCities([]);
            setSelectedCity("");
            return;
        }

        const citiesByState = [
            ...new Set(
                cities
                    .filter(c => c.state === selectedState)
                    .map(c => c.city)
                    .filter(Boolean)
            )
        ].sort();

        setFilteredCities(citiesByState);
    }, [selectedState, cities]);

    useEffect(() => {
        if (!selectedState && !selectedCity) {
            setFilteredProperties(allProperties);
            return;
        }

        let filtered = rawProperties;

        if (selectedState) {
            filtered = filtered.filter(p => p.state === selectedState);
        }

        if (selectedCity) {
            filtered = filtered.filter(p => p.city === selectedCity);
        }

        const normalizedFiltered = filtered.map(p => ({
            label: p.nickName,
            value: p.parking_Lot_ID,
            city: p.city,
            state: p.state,
            zipcode: p.zipcode
        }));

        setFilteredProperties(normalizedFiltered);
    }, [selectedState, selectedCity, rawProperties, allProperties]);

    // ================= RENDER =================

    return (
        <>
                        <TableContainer
                component={Paper}
                className="pricing-table-container"
            >
                <Table  className="pricing-table">

                    <TableHead>
                        <TableRow>
                            <TableCell>Pricing Plan</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Pricing Rules</TableCell>
                            <TableCell>Price Range</TableCell>
                            <TableCell>Properties Assigned</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rowsData.length > 0 ? (
                            rowsData.map((row) => (
                                <TableRow key={row.pricingPlanID}>
                                    <TableCell>
                                        {row.pricingPlanName}
                                        {row.isDefault && <span className="default-badge">Default</span>}
                                    </TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>
                                        {`${row.rules?.length || 0} ${row.rules?.length === 1 ? "Rule" : "Rules"}`}
                                    </TableCell>
                                    <TableCell>{getPriceRange(row.rules)}</TableCell>
                                    <TableCell>
                                        <span
                                            style={{ textDecoration: "underline", cursor: "pointer" }}
                                            onClick={() => handleViewProperties(row)}
                                        >
                                            {row.propertyCount ?? 0} Properties
                                        </span>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 170 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "6px" }}>
                                            <span
                                                style={{
                                                    textDecoration: "underline",
                                                    color: "#1976d2",
                                                    cursor: "pointer",
                                                    whiteSpace: "nowrap"
                                                }}
                                                onClick={() => handleAssignClick(row)}
                                            >
                                                Assign Properties
                                            </span>

                                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </TableCell>

                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No Pricing Plans Found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ================= MENU ================= */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem
                    onClick={() => {
                        onEditPlan(selectedRowForMenu);
                        handleMenuClose();
                    }}
                >
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => handleDeletePlan(selectedRowForMenu)}
                    sx={{ color: "error.main" }}
                >
                    Delete
                </MenuItem>
            </Menu>

            {/* ================= DELETE DIALOG ================= */}

            <Dialog
    classes={{ paper: 'custom-paper-class-delete' }}
    aria-labelledby="customized-dialog-title"
    dividers
    open={openDeleteDialog}
    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
>
    <DialogTitle
        id="customized-dialog-title"
        className="delete-dialog-title"
    >
        Are You Sure You Want to Delete This Pricing Plan?
    </DialogTitle>

    <DialogContent className="delete-dialog-content">
        <Typography gutterBottom>
            Once deleted, this action is irreversible.
            Please confirm deleting{" "}
            <strong>{planToDelete?.pricingPlanName}</strong>.
        </Typography>

        {/* Show property count warning if any properties assigned */}
        {planToDelete?.propertyCount > 0 && (
            <Typography
                gutterBottom
                className="delete-warning-box"
            >
                ⚠️ <strong>{planToDelete.propertyCount} {planToDelete.propertyCount === 1 ? "property is" : "properties are"}</strong> assigned to this plan.{" "}
                {planToDelete?.isDefault
                    ? "Since this is the default plan, those properties will become unassigned."
                    : "They will be automatically moved to the default plan."}
            </Typography>
        )}
    </DialogContent>

    <DialogActions className="delete-dialog-actions">
        <Button
            className='custom-button-cancel'
            onClick={() => handleDeleteConfirm('cancel')}
        >
            Cancel
        </Button>

        <Button
            className='custom-button-delete'
            onClick={() => handleDeleteConfirm('confirm')}
        >
            Delete
        </Button>
    </DialogActions>
</Dialog>


            {/* ================= VIEW PROPERTIES DIALOG ================= */}
            <Dialog open={openPropertiesPopup} onClose={() => setOpenPropertiesPopup(false)} maxWidth="md" fullWidth>
                <DialogTitle className='assign-dialog-title-primary'>Assigned Properties</DialogTitle>
                <DialogContent sx={{ paddingBottom: 0 }}>
                    <Paper elevation={0} className="view-props-summary-paper">
                        <Typography  className="assign-plan-info-name" variant="subtitle2" fontWeight={600}>
                            {selectedRow?.pricingPlanName}
                        </Typography>
                      
                    </Paper>
                </DialogContent>
                <DialogContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Property Name</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>Zip Code</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedRow?.properties?.length > 0 ? (
                                selectedRow.properties.map((p, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{p.nickName}</TableCell>
                                        <TableCell>{p.parkingLotAddress}</TableCell>
                                        <TableCell>{p.zipcode}</TableCell>
                                        <TableCell>
                                            <span
                                                className="remove-link"
                                                onClick={() => handleRemoveProperty(p)}
                                            >
                                                Remove
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No Properties Assigned
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions
                   className="view-props-dialog-actions"
                >
                    <Button
                        className="button-close"
                        onClick={() => setOpenPropertiesPopup(false)}
                    >
                        Close
                    </Button>
                </DialogActions>

            </Dialog>

            {/* ================= ASSIGN PROPERTIES DIALOG ================= */}
            <Dialog open={assignPropertiesPopup} onClose={() => setassignPropertiesPopup(false)} PaperProps={{ sx: { width: 584, borderRadius: "10px",p: "24px" } }}>
                <DialogTitle sx={{ p: 0 }}>
                    <Typography className="assign-dialog-title-primary">Assign Properties</Typography>
                    <Typography className="assign-dialog-title-secondary" color="text.secondary">
                        Choose how you want to apply this pricing plan by ZIP code or by specific properties
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 0, pt: 2 }}>
                    <Typography className="assign-plan-label">Pricing Plan</Typography>
                    <Paper elevation={0} className="assign-plan-info-paper">
                        <Typography className="assign-plan-info-name">{selectedRow?.pricingPlanName}</Typography>
                       
                    </Paper>

                    <Typography  className="assign-how-label">How would you like to assign this plan?</Typography>
                    <RadioGroup
                        row
                        value={assignType}
                        onChange={(e) => {
                            const value = e.target.value;
                            setAssignType(value);
                            if (value !== "zip") setSelectedZipCodes([]);
                            if (value !== "property") {
                                setSelectedProperties([]);
                                setSelectedCity('');
                                setSelectedState('');
                            }
                        }}
                        sx={{ mb: 1 }}
                    >
                        <FormControlLabel value="zip" control={<Radio
                            sx={{
                                color: "#1976d2",
                                "&.Mui-checked": { color: "#1976d2" },
                            }}
                        />} label="Assign by ZIP Code" />
                        <FormControlLabel value="property" control={<Radio
                            sx={{
                                color: "#1976d2",
                                "&.Mui-checked": { color: "#1976d2" },
                            }}
                        />} label="Assign to Specific Properties" />
                    </RadioGroup>

                    {assignType === "zip" && (
                        <Box sx={{ width: 280, mt: 0.5 }}>
                            <Typography fontWeight={400} fontSize={16} mb={0.5}>Select ZIP Code</Typography>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect  // ← ADD THIS
                                options={[SELECT_ALL, ...allZipCodes]}
                                value={selectedZipCodes}
                                getOptionLabel={getOptionLabel}
                                isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
                                onChange={(event, newValue) => {
                                    const selectedValues = newValue
                                        .map(getOptionValue)
                                        .filter(value => value !== SELECT_ALL.value);

                                    if (newValue.some(option => getOptionValue(option) === SELECT_ALL.value)) {
                                        setSelectedZipCodes(selectedZipCodes.length === allZipCodes.length ? [] : allZipCodes);
                                    } else {
                                        setSelectedZipCodes(selectedValues);
                                    }
                                }}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox checked={getOptionValue(option) === SELECT_ALL.value ? selectedZipCodes.length === allZipCodes.length : selected} />
                                        {getOptionLabel(option)}
                                    </li>
                                )}
                                renderInput={(params) => <TextField {...params} placeholder="Search by ZIP Code" size="small" />}
                                renderTags={(value) => {
                                    if (!value.length) return null;
                                    return (
                                        <>
                                            <Chip label={value[0]} size="small" />
                                            {value.length > 1 && <Chip label={`+${value.length - 1}`} size="small" />}
                                        </>
                                    );
                                }}
                            />
                        </Box>
                    )}

                    {assignType === "property" && (
                        <>
                            <Box className="property-filter-row">

                                <Typography
                                   className="property-filter-label"
                                >
                                    Filter By :
                                </Typography>

                                <Autocomplete options={states} value={selectedState} onChange={(e, val) => { setSelectedState(val); setSelectedCity(null); }} renderInput={(params) => <TextField {...params} placeholder="State" size="small" />} sx={{ minWidth: 150 }} />
                                <Autocomplete options={filteredCities} value={selectedCity} disabled={!selectedState} onChange={(e, val) => setSelectedCity(val)} renderInput={(params) => <TextField {...params} placeholder="City" size="small" />} sx={{ minWidth: 150 }} />
                            </Box>
                            <Box className="property-select-box">
                                <Typography className="property-select-label">Select Property</Typography>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect  // ← ADD THIS
                                    options={[SELECT_ALL, ...filteredProperties]}
                                    value={selectedProperties}
                                    getOptionLabel={getOptionLabel}
                                    isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
                                    onChange={(event, newValue) => {
                                        if (newValue.some(v => getOptionValue(v) === SELECT_ALL.value)) {
                                            setSelectedProperties(selectedProperties.length === filteredProperties.length ? [] : filteredProperties);
                                        } else {
                                            setSelectedProperties(newValue.filter(value => getOptionValue(value) !== SELECT_ALL.value));
                                        }
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox checked={getOptionValue(option) === SELECT_ALL.value ? selectedProperties.length === filteredProperties.length : selected} />
                                            {getOptionLabel(option)}
                                        </li>
                                    )}
                                    renderInput={(params) => <TextField {...params} placeholder="Search by Property" size="small" />}
                                    renderTags={(value) => {
                                        if (!value.length) return null;
                                        return (
                                            <>
                                                <Chip label={value[0].label} size="small" />
                                                {value.length > 1 && <Chip label={`+${value.length - 1}`} size="small" />}
                                            </>
                                        );
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>

                {assignError && (
                    <Typography color="error" sx={{ mt: 1, fontSize: 14 }}>
                        {assignError}
                    </Typography>
                )}

                <DialogActions
                    className="assign-dialog-actions" sx={{ p: 0, pt: 2 }}
                >
                    <Button
                        variant="outlined"
                        className="assign-cancel-btn"
                        onClick={() => setassignPropertiesPopup(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        className="assign-plan-btn"
                        onClick={handleAssignPlan}
                    >
                        Assign Plan
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PricingPlanList;